/* eslint-disable no-unused-expressions */
import React, {useState, useEffect} from 'react';
import wrap from 'wraptile';
import {useSelector} from 'react-redux';
import {curry} from 'ramda';
import {selectRatings} from '@pluto/rating-select/Redux/ratingSelectors';
import AppBar from '@material-ui/core/AppBar';
import Tab from '@material-ui/core/Tab';
import TabContext from '@material-ui/lab/TabContext';
import Tabs from '@material-ui/core/Tabs';
import TabPanel from '@material-ui/lab/TabPanel';
import QCMetadataTerritorialTab from './QCMetadataTerritorialTab';
import {delayAndSetTab} from './QCMetadataLocalized';
import {
    MetadataTerritorial,
    SetCurrentTab,
    TabLabelGetter,
    HandleChangeEvent,
    Descriptor,
} from './QCToolsMetadataTypes';
import {getHeaderEditBlock, openEditMode} from './QCMetadataUtils';
import {TerritorialMetadataUpdateHandlers} from './QCToolsMetadataContainer';

export interface QCMetadataTerritorialProps {
    territorial: MetadataTerritorial;
    isEditMode: boolean;
    setIsEditMode: (value: boolean) => void;
    ratingDescriptors: Descriptor[];
    updateTerritorialMetadata: (params: TerritorialMetadataUpdateHandlers) => void;
    taskId: number;
    jobDeliveryDate: string;
}

export interface TerritorialMetadataType {
    countryCode: string;
    rating?: string;
    captionsRequired?: boolean;
    ratingDescriptors?: string[];
    targetAge?: string;
    contentItemId?: number;
    [index: string]: any;
}

export const checkCaptions = (captionRequired: boolean) =>
    captionRequired ? 'Yes' : 'No';

export const getTerritorialTabLabel = (key: string | undefined): string => {
    return key?.toUpperCase() ?? '';
};

export const handleChange = curry(
    (setCurrentTab: SetCurrentTab, event: HandleChangeEvent, newValue: string) => {
        setCurrentTab(newValue);
    }
);

export const mapToTab = curry(
    (getTerritorialTabLabel: TabLabelGetter, value: string | undefined, i: number) => {
        const label = getTerritorialTabLabel(value);

        return (
            <Tab label={label} value={value} key={value} id={`territorial-tab-${i}`} />
        );
    }
);

export const getTabs = (getTerritorialTabLabel: TabLabelGetter, keys: string[]) => {
    const withLabel = mapToTab(getTerritorialTabLabel);
    return keys.map(withLabel);
};

export const setClassNotValidTab = (
    isValid: boolean,
    tab: HTMLElement,
    header: HTMLElement
) => {
    if (!isValid && tab && header) {
        tab.classList.add('not-valid-tab');
        header.classList.add('not-valid-header');
    } else if (isValid && tab && header) {
        tab.classList.remove('not-valid-tab');
        header.classList.remove('not-valid-header');
    }
};

export const getIsTabValidByRating = (ratings: {[key: string]: string}, rating: string) => {
    const ratingsValues = Object.keys(ratings);

    return Boolean(rating) && ratingsValues.includes(rating)
}

export const mapToTabsPanel = curry(
    (
        territorial,
        setTerritorialMetadata: (val: TerritorialMetadataType) => void,
        isEditMode,
        enableRatingDescriptors: Descriptor[],
        ratings: {[key: string]: string},
        setValidTabs: (val: boolean) => void,
        cancelEditMode: boolean,
        key,
        index
    ) => {
        const {rating, captionRequired, ratingDescriptors, targetAge} = territorial[key];
        const isCaptionsRequired = checkCaptions(captionRequired);

        let isTabValid = getIsTabValidByRating(ratings, rating);

        if (key === 'us') {
            isTabValid = Boolean(rating && targetAge);
        }

        if (enableRatingDescriptors.length) {
            // let UI render components we need
            setTimeout(
                delayAndSetTab(index, isTabValid, 'territorial', setClassNotValidTab),
                200
            );
        }

        return (
            <TabPanel value={key} key={key} id={key} className="territorial-tab">
                <QCMetadataTerritorialTab
                    targetAge={targetAge}
                    rating={rating}
                    countryCode={key}
                    isEditMode={isEditMode}
                    captionRequired={isCaptionsRequired}
                    ratingDescriptors={ratingDescriptors}
                    enableRatingDescriptors={enableRatingDescriptors}
                    setTerritorialMetadata={setTerritorialMetadata}
                    setValidTabs={setValidTabs}
                    cancelEditMode={cancelEditMode}
                />
            </TabPanel>
        );
    }
);

export const getTabsPanel = (
    territorial: MetadataTerritorial,
    keys: string[],
    setTerritorialMetadata: (val: TerritorialMetadataType) => void,
    isEditMode: boolean,
    ratingDescriptors: Descriptor[],
    ratings: {[key: string]: any},
    setValidTabs: (val: boolean) => void,
    cancelEditMode: boolean,
) => {
    const withTerritorial = mapToTabsPanel(
        territorial,
        setTerritorialMetadata,
        isEditMode,
        ratingDescriptors,
        ratings,
        setValidTabs,
        cancelEditMode,
    );

    return keys.map(withTerritorial);
};

export const mapTerritorialValues = curry(
    (id: number, territorialMetadata: TerritorialMetadataType, key: string) => ({
        ...territorialMetadata[key],
        countryCode: key,
        id,
    })
);

export const saveEditedChanges = wrap(
    (
        setIsEditMode: (value: boolean) => void,
        territorialMetadata: TerritorialMetadataType,
        id: number,
        updateTerritorialMetadata: (params: TerritorialMetadataUpdateHandlers) => void,
        setCancelEditMode: (val: boolean) => void,
    ) => {
        const body = Object.keys(territorialMetadata).map(
            mapTerritorialValues(id, territorialMetadata) as any
        );
        const params = {
            body,
            id,
        };
        updateTerritorialMetadata(params as TerritorialMetadataUpdateHandlers);
        setIsEditMode(false);
        setCancelEditMode(false);
    }
);

export const cancelEditedChanges = wrap(
    (
        setIsEditMode: (value: boolean) => void,
        setCancelEditMode: (value: boolean) => void
    ) => {
        setIsEditMode(false);
        setCancelEditMode(true);
    }
);

export const handleEditChange = wrap(
    (isEditMode: boolean, setCancelEditMode: (val: boolean) => void) => {
        if (isEditMode) {
            setCancelEditMode(false);
        }
    }
);

export default function QCMetadataTerritorial(props: QCMetadataTerritorialProps) {
    const {
        territorial,
        isEditMode,
        setIsEditMode,
        taskId,
        ratingDescriptors,
        updateTerritorialMetadata,
        jobDeliveryDate,
    } = props;
    const territorialTabsKeys: string[] = Object.keys(territorial);
    const [value, setValue] = useState<string>(territorialTabsKeys[0]);
    const [validTabs, setValidTabs] = useState(true);
    const [territorialMetadata, setTerritorialMetadata] = useState<
        TerritorialMetadataType | {}
    >(territorial);
    const ratings = useSelector(selectRatings);
    const [cancelEditMode, setCancelEditMode] = useState<boolean>(false);

    useEffect(handleEditChange(isEditMode, setCancelEditMode), [isEditMode]);

    if (territorialTabsKeys.length === 0) {
        return (
            <>
                <div className="qc-metadata__territorial-header">
                    Territorial metadata
                </div>
                <div className="qc-metadata__territorial-no-data">No Data</div>
            </>
        );
    }

    const onHandleChange = handleChange(setValue);
    const tabs = getTabs(getTerritorialTabLabel, territorialTabsKeys);
    const tabsPanel = getTabsPanel(
        territorial,
        territorialTabsKeys,
        setTerritorialMetadata,
        isEditMode,
        ratingDescriptors,
        ratings,
        setValidTabs,
        cancelEditMode,
    );

    return (
        <>
            <div className="qc-metadata__territorial-header">
                <div id="territorial-header">Territorial metadata</div>
                {!jobDeliveryDate &&
                    getHeaderEditBlock(
                        isEditMode,
                        validTabs,
                        openEditMode(setIsEditMode, setCancelEditMode),
                        saveEditedChanges(
                            setIsEditMode,
                            territorialMetadata,
                            taskId,
                            updateTerritorialMetadata,
                            setCancelEditMode,
                        ),
                        cancelEditedChanges(setIsEditMode, setCancelEditMode)
                    )}
            </div>
            <div className="qc-metadata__territorial-body">
                <TabContext value={value}>
                    <AppBar position="static">
                        <Tabs
                            value={value}
                            onChange={onHandleChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="scrollable territorial tabs"
                        >
                            {tabs}
                        </Tabs>
                    </AppBar>
                    {tabsPanel}
                </TabContext>
            </div>
        </>
    );
}
